import { clientLocalDate } from './clientContext'
import { supabase } from './supabaseClient'

export type DailyWorkoutStatus = 'not_started' | 'in_progress' | 'completed'

export type DailyWorkoutLog = {
  id: string
  user_id: string
  schedule_id: string | null
  log_date: string
  status: DailyWorkoutStatus
  started_at: string | null
  completed_at: string | null
}

export function todayLogDate(): string {
  return clientLocalDate()
}

export async function fetchTodayWorkoutLog(userId: string): Promise<{ log: DailyWorkoutLog | null; error: string | null }> {
  const logDate = todayLogDate()
  const { data, error } = await supabase
    .from('daily_workout_logs')
    .select('id, user_id, schedule_id, log_date, status, started_at, completed_at')
    .eq('user_id', userId)
    .eq('log_date', logDate)
    .maybeSingle()

  if (error) return { log: null, error: error.message }
  if (!data) return { log: null, error: null }

  return {
    log: {
      id: data.id,
      user_id: data.user_id,
      schedule_id: data.schedule_id,
      log_date: data.log_date,
      status: data.status as DailyWorkoutStatus,
      started_at: data.started_at,
      completed_at: data.completed_at,
    },
    error: null,
  }
}

export async function fetchWorkoutLogHistory(userId: string): Promise<{ logs: DailyWorkoutLog[]; error: string | null }> {
  const { data, error } = await supabase
    .from('daily_workout_logs')
    .select('id, user_id, schedule_id, log_date, status, started_at, completed_at')
    .eq('user_id', userId)
    .order('log_date', { ascending: false })
    .limit(30)

  if (error) return { logs: [], error: error.message }

  return {
    logs: (data ?? []).map((row) => ({
      id: row.id,
      user_id: row.user_id,
      schedule_id: row.schedule_id,
      log_date: row.log_date,
      status: row.status as DailyWorkoutStatus,
      started_at: row.started_at,
      completed_at: row.completed_at,
    })),
    error: null,
  }
}

export async function startDailyWorkout(userId: string, scheduleId: string): Promise<{ log: DailyWorkoutLog | null; error: string | null }> {
  const now = new Date().toISOString()
  const logDate = todayLogDate()

  const { data, error } = await supabase
    .from('daily_workout_logs')
    .upsert(
      {
        user_id: userId,
        schedule_id: scheduleId,
        log_date: logDate,
        status: 'in_progress',
        started_at: now,
        updated_at: now,
      },
      { onConflict: 'user_id,log_date' },
    )
    .select('id, user_id, schedule_id, log_date, status, started_at, completed_at')
    .single()

  if (error) return { log: null, error: error.message }

  return {
    log: {
      id: data.id,
      user_id: data.user_id,
      schedule_id: data.schedule_id,
      log_date: data.log_date,
      status: data.status as DailyWorkoutStatus,
      started_at: data.started_at,
      completed_at: data.completed_at,
    },
    error: null,
  }
}

export async function completeDailyWorkout(userId: string, scheduleId: string): Promise<{ log: DailyWorkoutLog | null; error: string | null }> {
  const now = new Date().toISOString()
  const logDate = todayLogDate()

  const { data: existing } = await supabase
    .from('daily_workout_logs')
    .select('started_at')
    .eq('user_id', userId)
    .eq('log_date', logDate)
    .maybeSingle()

  const { data, error } = await supabase
    .from('daily_workout_logs')
    .upsert(
      {
        user_id: userId,
        schedule_id: scheduleId,
        log_date: logDate,
        status: 'completed',
        started_at: existing?.started_at ?? now,
        completed_at: now,
        updated_at: now,
      },
      { onConflict: 'user_id,log_date' },
    )
    .select('id, user_id, schedule_id, log_date, status, started_at, completed_at')
    .single()

  if (error) return { log: null, error: error.message }

  return {
    log: {
      id: data.id,
      user_id: data.user_id,
      schedule_id: data.schedule_id,
      log_date: data.log_date,
      status: data.status as DailyWorkoutStatus,
      started_at: data.started_at,
      completed_at: data.completed_at,
    },
    error: null,
  }
}

export function resolveSessionView(
  log: DailyWorkoutLog | null,
  logHistoryEmpty: boolean,
): 'not_started' | 'in_progress' | 'completed' {
  if (!log || logHistoryEmpty) return 'not_started'
  if (log.status === 'completed') return 'completed'
  if (log.status === 'in_progress') return 'in_progress'
  return 'not_started'
}
